import React from "react";
import { Box, useMediaQuery, useTheme, Avatar } from "@mui/material";
import { Check } from "@mui/icons-material";
import { makeStyles } from "@mui/styles";
import { COLORS } from "components/v3/Theme/colors";
import { SurveyModel } from "survey-core";
import { FONT_CONSTANTS } from "constants/StyleConstants";

const useStyles = makeStyles(() => ({
  surveyStepper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "30px",
    padding: "20px 0",
    marginBottom: "10px"
  },
  stepperItem: {
    display: "flex",
    alignItems: "center",
    gap: "10px"
  },
  stepperLabel: {
    fontSize: "14px",
    fontWeight: "700",
    color: COLORS.BLACK,
    paddingTop: "2px"
  }
}));

export interface SurveyPage {
  name: string;
  title: string;
  index: number;
}

export interface SurveyStepperProps {
  pages: SurveyPage[];
  currentPageIndex: number;
  surveyModel?: SurveyModel;
}

const SurveyStepper: React.FC<SurveyStepperProps> = ({
  pages,
  currentPageIndex,
  surveyModel
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  if (!pages || pages.length === 0) {
    return null;
  }

  // Filter pages based on their visibility conditions
  const getVisiblePages = (): SurveyPage[] => {
    if (!surveyModel) {
      return pages; // If no survey model, show all pages
    }

    return pages.filter((page) => {
      const surveyPage = surveyModel.getPageByName(page.name);
      if (!surveyPage) {
        return false;
      }

      // Check if the page is visible based on its visibleIf condition
      return surveyPage.isVisible;
    });
  };

  const visiblePages = getVisiblePages();

  return (
    <Box className={classes.surveyStepper}>
      {visiblePages.map((page, visibleIndex) => {
        // Find the original index of this page in the full pages array
        const originalIndex = pages.findIndex((p) => p.name === page.name);
        const isCompleted = originalIndex < currentPageIndex;
        const isActive = originalIndex === currentPageIndex;

        const dotColor = isCompleted
          ? COLORS.PRIMARY
          : isActive
          ? COLORS.ACCENT2
          : COLORS.DARK_GRAY;

        const labelColor = isCompleted
          ? COLORS.PRIMARY
          : isActive
          ? COLORS.ACCENT2
          : COLORS.DARK_GRAY;

        return (
          <Box key={page.name} className={classes.stepperItem}>
            <Avatar
              sx={{
                bgcolor: isActive ? COLORS.ACCENT2_LIGHT : "transparent",
                width: 25,
                height: 25,
                fontSize: 15,
                overflow: "visible",
                animation: isActive ? "pulse 3s infinite ease-in-out" : "none",
                "@keyframes pulse": {
                  "0%, 100%": {
                    boxShadow: "0 0 0 0"
                  },
                  "50%": {
                    boxShadow: `0 0 0 5px ${COLORS.ACCENT2_LIGHT}`
                  }
                }
              }}
            >
              <Avatar
                sx={{
                  bgcolor: dotColor,
                  width: "20px",
                  height: "18px",
                  fontSize: 14,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  paddingTop: "2px"
                }}
              >
                {isCompleted ? (
                  <Check sx={{ fontSize: 13, position: "relative", top: -1 }} />
                ) : (
                  visibleIndex + 1
                )}
              </Avatar>
            </Avatar>
            {!isMobile && (
              <Box
                sx={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: labelColor,
                  paddingTop: "2px",
                  fontFamily: FONT_CONSTANTS.FONT_AVENIRLT_PRO_HEAVY
                }}
              >
                {page.title}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default SurveyStepper;
